import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../../face-identity/components/FaceIdentityFlowNav';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useFaceIdentityScrollShell } from '../../face-identity/hooks/useFaceIdentityScrollShell';
import {
  fetchActiveVisit,
  fetchGuestVisitSummary,
  fetchOpenVisits,
  patchCheckinVisitIntent,
} from '../../salon-admin/utils/checkinQueueApi';
import { fetchFranchiseStaffCatalog } from '../../admin-shared/franchiseStaffCatalogApi';
import {
  SALON_HUB_PATH,
  SALON_SERVICE_LOBBY_PATH,
  SALON_SERVICE_SESSION_PATH,
  SALON_SERVICE_WORK_PATH,
} from '../config/salonApp';
import { pendingAnalysisLines } from '../utils/analysisServiceSync';
import { resolveSalonHomePath } from '../utils/salonHomeNav';
import { setActiveCheckinSession } from '../utils/salonFaceSession';
import { collectOccupiedStylistIds, ongoingLines } from '../utils/serviceLineUtils';
import {
  readVisitIntentOrNull,
  VISIT_INTENT_ANALYZE,
  VISIT_INTENT_SERVICE,
} from '../utils/visitIntent';
import { buildAnalysisNavState } from '../utils/visitHandoff';
import { KIOSK_MODE_DESK, setSalonKioskMode } from '../utils/salonKioskMode';
import {
  staffDeskPickSubtitle,
  staffDeskUnavailable,
} from '../utils/staffDeskPick';
import '../../face-identity/styles/FaceIdentityLuxTheme.css';
import '../../face-identity/styles/FaceIdentityScrollShell.css';
import './ServiceSessionPage.css';
import './VisitIntentPage.css';

async function fetchStaffCatalog() {
  return fetchFranchiseStaffCatalog();
}

function formatHistoryDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).slice(0, 10);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function uniquePastServices(history, currentCheckinId, limit = Number.POSITIVE_INFINITY) {
  const seen = new Set();
  const rows = [];
  for (const row of history || []) {
    if (currentCheckinId && String(row.visit_id) === String(currentCheckinId)) continue;
    const name = String(row.service_name || row.name || '').trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    seen.add(name.toLowerCase());
    rows.push({ name, at: row.selected_at });
    if (rows.length >= limit) break;
  }
  return rows;
}

function collectPastServices(lastVisit, historyServices, currentCheckinId) {
  if (lastVisit?.services?.length) {
    return lastVisit.services
      .map((name) => ({
        name: String(name || '').trim(),
        at: lastVisit.completed_at,
      }))
      .filter((row) => row.name);
  }
  return uniquePastServices(
    historyServices.map((h) => ({ ...h, name: h.service_name })),
    currentCheckinId,
  );
}

export default function StylistGuestPickPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  useFaceIdentityScrollShell(false);
  const homePath = resolveSalonHomePath();
  const checkinId = location.state?.checkinId;

  const [checkin, setCheckin] = useState(null);
  const [lastVisit, setLastVisit] = useState(null);
  const [historyServices, setHistoryServices] = useState([]);
  const [staffCatalog, setStaffCatalog] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [stylistIntent, setStylistIntent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [occupiedStylistIds, setOccupiedStylistIds] = useState(() => new Set());
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const allPastServices = useMemo(
    () => collectPastServices(lastVisit, historyServices, checkin?.id),
    [lastVisit, historyServices, checkin?.id],
  );
  const previewPastServices = useMemo(() => allPastServices.slice(0, 3), [allPastServices]);
  const hasMorePastServices = allPastServices.length > 3;
  const lastVisitDate = useMemo(
    () => (lastVisit?.completed_at ? formatHistoryDate(lastVisit.completed_at) : ''),
    [lastVisit],
  );

  useEffect(() => {
    setSalonKioskMode(KIOSK_MODE_DESK);
  }, []);

  const refreshStylistDeskState = useCallback(async () => {
    if (!checkinId) return;
    try {
      const [open, staff] = await Promise.all([fetchOpenVisits(), fetchStaffCatalog()]);
      setOccupiedStylistIds(collectOccupiedStylistIds(open, checkinId));
      setStaffCatalog(staff);
    } catch {
      try {
        const open = await fetchOpenVisits();
        setOccupiedStylistIds(collectOccupiedStylistIds(open, checkinId));
      } catch {
        /* non-blocking */
      }
    }
  }, [checkinId]);

  const load = useCallback(async () => {
    if (!checkinId) return;
    setLoading(true);
    setError('');
    try {
      const row = await fetchActiveVisit({ checkinId });
      if (!row?.id) throw new Error('Guest visit not found');
      setCheckin(row);
      setStylistIntent(readVisitIntentOrNull(row));
      setActiveCheckinSession({
        id: row.id,
        code: row.customer_code,
        status: row.status,
        faceUserId: row.face_user_id,
      });
      setLoading(false);
      setDetailsLoading(true);
      try {
        const { summary, serviceHistory } = await fetchGuestVisitSummary({ checkinId: row.id });
        setLastVisit(summary?.last_visit || null);
        setHistoryServices(serviceHistory || []);
      } catch {
        /* history optional */
      }
      try {
        const [staff, open] = await Promise.all([fetchStaffCatalog(), fetchOpenVisits()]);
        const occupied = collectOccupiedStylistIds(open, row.id);
        setOccupiedStylistIds(occupied);
        setStaffCatalog(staff);
        // The booked stylist (check-in from an engagement booking) is preselected when free; still changeable.
        const booked = Array.isArray(row.assigned_staff) ? row.assigned_staff[0] : null;
        const match = booked ? (staff || []).find((p) => String(p.id) === String(booked.id)) : null;
        if (match && !occupied.has(String(match.id))) {
          setSelectedStaff((prev) => prev || match);
        }
        if (staff.length === 1 && !staffDeskUnavailable(staff[0], occupied)) {
          setSelectedStaff({ id: staff[0].id, name: staff[0].name });
        }
      } catch (err) {
        setError(err?.message || 'Could not load stylist list');
      } finally {
        setDetailsLoading(false);
      }
    } catch (err) {
      setError(err?.message || 'Could not load guest');
      setLoading(false);
      setDetailsLoading(false);
    }
  }, [checkinId]);

  useEffect(() => {
    if (!checkinId) {
      navigate(SALON_SERVICE_LOBBY_PATH, { replace: true });
      return;
    }
    load();
  }, [checkinId, load, navigate]);

  useEffect(() => {
    if (!checkinId) return undefined;
    const onQueue = () => { refreshStylistDeskState(); };
    window.addEventListener('checkin-queue-updated', onQueue);
    const timer = window.setInterval(refreshStylistDeskState, 12000);
    return () => {
      window.removeEventListener('checkin-queue-updated', onQueue);
      window.clearInterval(timer);
    };
  }, [checkinId, refreshStylistDeskState]);

  useEffect(() => {
    if (!selectedStaff) return;
    const row = staffCatalog.find((p) => String(p.id) === String(selectedStaff.id));
    if (staffDeskUnavailable(row || selectedStaff, occupiedStylistIds)) {
      setSelectedStaff(null);
    }
  }, [occupiedStylistIds, selectedStaff, staffCatalog]);

  const startWithStylist = async () => {
    if (!selectedStaff || !checkin?.id) {
      setError('Select your name to continue');
      return;
    }
    if (!stylistIntent) {
      setError('Choose service list or skin & hair analysis');
      return;
    }
    const stylistId = String(selectedStaff.id);
    const stylistName = selectedStaff.name;
    const ongoing = ongoingLines(checkin.selected_services, stylistId);
    if (ongoing.length) {
      navigate(SALON_SERVICE_WORK_PATH, {
        state: {
          checkinId: checkin.id,
          checkinCode: checkin.customer_code,
          customerName: checkin.customer_name,
          stylistId,
          stylistName,
        },
      });
      return;
    }

    setBusy(true);
    setError('');
    try {
      const currentIntent = readVisitIntentOrNull(checkin);
      if (currentIntent !== stylistIntent) {
        const updated = await patchCheckinVisitIntent(checkin.id, stylistIntent);
        setCheckin(updated);
      }
      // Do not assign/lock stylist yet — lock only when Start service runs.
      const fresh = await fetchActiveVisit({ checkinId: checkin.id });
      const pending = pendingAnalysisLines(fresh?.selected_services || checkin.selected_services);
      const base = buildAnalysisNavState(fresh || checkin, {
        stylistId,
        stylistName,
        visitIntent: stylistIntent,
        prefillPendingServices: pending,
        fromStylistDesk: true,
      });
      if (stylistIntent === VISIT_INTENT_ANALYZE) {
        navigate(SALON_HUB_PATH, { state: base });
        return;
      }
      navigate(SALON_SERVICE_SESSION_PATH, { state: base });
    } catch (err) {
      setError(err?.message || 'Could not start');
    } finally {
      setBusy(false);
    }
  };

  const guestName = checkin?.customer_name || checkin?.customer_snapshot?.name || 'Guest';
  const snap = checkin?.customer_snapshot || {};
  const guestMobile = checkin?.mobile || snap.mobile || '';

  return (
    <div
      className="fi-lux-theme fi-scroll-page page-bg salon-service-session salon-service-session--flow salon-service-session--guest-pick salon-service-session--guest-pick-fixed"
    >
      <div className="salon-service-session__content salon-guest-pick">
        <header className="salon-service-session__brand">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
          />
          <FaceIdentityFlowNav
            homePath={homePath}
            onBack={() => navigate(SALON_SERVICE_LOBBY_PATH)}
          />
        </header>

        <div className="salon-guest-pick__sheet">
        <section className="salon-guest-pick__hero">
          <p className="salon-guest-pick__eyebrow">Guest</p>
          <h1 className="salon-service-session__title salon-guest-pick__name">{guestName}</h1>
          <p className="salon-service-session__lede salon-guest-pick__meta">
            {checkin?.customer_code ? (
              <>
                Customer code <strong>{checkin.customer_code}</strong>
              </>
            ) : (
              'Customer check-in'
            )}
            {guestMobile ? ` · ${guestMobile}` : ''}
          </p>
        </section>

        {error ? <p className="salon-service-session__error">{error}</p> : null}

        {loading ? (
          <p className="salon-service-session__lede">Loading customer…</p>
        ) : (
          <div className="salon-guest-pick__body">
            {detailsLoading ? (
              <p className="salon-service-session__lede">Loading visit history…</p>
            ) : null}
            <section className="salon-service-session__panel salon-guest-pick__panel salon-guest-pick__panel--history">
              <div className="salon-guest-pick__panel-head">
                <h2 className="salon-service-session__subtitle">Last services</h2>
                <div className="salon-guest-pick__panel-head-actions">
                  {lastVisitDate ? (
                    <span className="salon-guest-pick__chip">{lastVisitDate}</span>
                  ) : null}
                  {hasMorePastServices ? (
                    <button
                      type="button"
                      className="salon-guest-pick__history-link"
                      onClick={() => setHistoryModalOpen(true)}
                    >
                      View all ({allPastServices.length})
                    </button>
                  ) : null}
                </div>
              </div>
              {previewPastServices.length ? (
                <ul className="salon-guest-pick__history salon-guest-pick__history--preview">
                  {previewPastServices.map((row) => (
                    <li key={`${row.name}-${row.at}`} className="salon-guest-pick__history-item">
                      <strong>{row.name}</strong>
                      {row.at ? <span>{formatHistoryDate(row.at)}</span> : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="salon-guest-pick__empty">No past services on file</p>
              )}
            </section>

            <section
              className="salon-service-session__panel salon-guest-pick__panel salon-guest-pick__panel--staff"
            >
              <h2 className="salon-service-session__subtitle">I am the stylist</h2>
              <div className="salon-guest-pick__staff-scroll">
                <div
                  className="salon-service-session__staff-grid salon-guest-pick__staff salon-guest-pick__staff--desk"
                  role="group"
                >
                  {staffCatalog.map((person) => {
                    const active = selectedStaff && String(selectedStaff.id) === String(person.id);
                    const unavailable = staffDeskUnavailable(person, occupiedStylistIds);
                    return (
                      <button
                        key={person.id}
                        type="button"
                        className={
                          active
                            ? 'salon-service-session__staff is-active'
                            : `salon-service-session__staff${unavailable ? ' is-occupied' : ''}`
                        }
                        onClick={() => {
                          if (unavailable) return;
                          setSelectedStaff({ id: person.id, name: person.name });
                        }}
                        disabled={busy || unavailable}
                        aria-disabled={unavailable || undefined}
                      >
                        <strong>{person.name}</strong>
                        <span>{staffDeskPickSubtitle(person, occupiedStylistIds)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            <section className="salon-service-session__panel salon-guest-pick__panel salon-guest-pick__panel--intent">
              <h2 className="salon-service-session__subtitle">Serve this guest</h2>
              <div className="salon-visit-intent__actions salon-guest-pick__intents">
                <button
                  type="button"
                  className={`salon-visit-intent__card${
                    stylistIntent === VISIT_INTENT_SERVICE ? ' is-active' : ''
                  }`}
                  disabled={busy}
                  onClick={() => setStylistIntent(VISIT_INTENT_SERVICE)}
                >
                  <span className="salon-visit-intent__card-title">Service list</span>
                  <span className="salon-visit-intent__card-hint">Pick services for this visit</span>
                </button>
                <button
                  type="button"
                  className={`salon-visit-intent__card${
                    stylistIntent === VISIT_INTENT_ANALYZE ? ' is-active' : ''
                  }`}
                  disabled={busy}
                  onClick={() => setStylistIntent(VISIT_INTENT_ANALYZE)}
                >
                  <span className="salon-visit-intent__card-title">Skin &amp; hair analysis</span>
                  <span className="salon-visit-intent__card-hint">Open the analysis hub</span>
                </button>
              </div>
            </section>

            <footer className="salon-service-session__footer salon-guest-pick__footer">
              <button
                type="button"
                className="salon-service-session__primary salon-service-session__primary--wide"
                disabled={busy || !selectedStaff || !stylistIntent}
                onClick={startWithStylist}
              >
                {busy ? 'Starting…' : 'Continue'}
              </button>
            </footer>
          </div>
        )}
        </div>
      </div>

      {historyModalOpen ? (
        <div
          className="salon-guest-pick__history-overlay"
          role="presentation"
          onClick={() => setHistoryModalOpen(false)}
        >
          <div
            className="salon-guest-pick__history-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Past services"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="salon-guest-pick__history-modal-head">
              <h2>Past services</h2>
              <button
                type="button"
                className="salon-guest-pick__history-close"
                onClick={() => setHistoryModalOpen(false)}
              >
                Close
              </button>
            </header>
            <ul className="salon-guest-pick__history salon-guest-pick__history--modal">
              {allPastServices.map((row) => (
                <li key={`modal-${row.name}-${row.at}`} className="salon-guest-pick__history-item">
                  <strong>{row.name}</strong>
                  {row.at ? <span>{formatHistoryDate(row.at)}</span> : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}
